import { useNavigate } from 'react-router-dom'
import { Button, ButtonVariant, ButtonStyle, Body } from '@meiuca/ui-components'
import { Calendar, Dumbbell, Menu, Repeat, X, MapPin, User, Wallet } from 'lucide-react'
import { useState } from 'react'
import { analyticsService } from '@meiuca/analytics'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents'
import { useUserLocation } from '@/hooks/useUserLocation'
import { useAuth } from '@/contexts/AuthContext'
import { AuthModal } from './AuthModal'
import { MarketplaceLocationPrompt } from './MarketplaceLocationPrompt'
import { UserMenu } from './UserMenu'
import { MeiucaLogo } from './MeiucaLogo'

interface MarketplaceHeaderProps {
  onLocationClick?: () => void
}

export function MarketplaceHeader({ onLocationClick }: MarketplaceHeaderProps) {
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [locationPromptOpen, setLocationPromptOpen] = useState(false)
  const { userLocation, getShortLocationName, hasLocation } = useUserLocation()
  const { user, isAuthenticated, isLoading: isAuthLoading, logout } = useAuth()

  // Pages can override with their own handler; otherwise the header owns a
  // self-contained location prompt so changing location works on every page.
  const handleLocationClick = onLocationClick ?? (() => setLocationPromptOpen(true))

  // Only show user menu for authenticated, non-anonymous users
  const displayUser = user && user.id !== 'anonymous' ? user : null
  const showUserMenu = !!displayUser && (isAuthenticated || isAuthLoading)

  const handleLogin = () => {
    setAuthModalOpen(true)
  }

  const handleAuthSuccess = () => {
    setAuthModalOpen(false)
  }

  const handleLogoClick = () => {
    navigate('/')
  }

  const trackNav = (item: string, source: 'header' | 'hamburger') => {
    analyticsService.track(ANALYTICS_EVENTS.NAV_CLICKED, { item, source })
  }

  // Account links shown in the mobile menu — the desktop UserMenu equivalent.
  // The header carries no product nav: Explore is reached from the home
  // content banner, and B2B links live in the footer.
  const accountItems = [
    { id: 'reservations', label: 'Minhas reservas', icon: Calendar, path: '/reservations' },
    { id: 'workouts', label: 'Meus treinos', icon: Dumbbell, path: '/treinos' },
    { id: 'plans', label: 'Meus planos', icon: Wallet, path: '/planos' },
    { id: 'memberships', label: 'Meus horários fixos', icon: Repeat, path: '/mensalistas' },
    { id: 'profile', label: 'Meu perfil', icon: User, path: '/perfil' }
  ]

  return (
    <header className="bg-background-white border-b border-border-light sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div
            className="flex items-center cursor-pointer"
            onClick={handleLogoClick}
          >
            <MeiucaLogo
              symbolClassName="h-9 w-9 text-brand-primary"
              wordmark={
                <span className="text-xl font-bold text-text-primary hidden sm:block">
                  Meiuca
                </span>
              }
            />
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            {/* Location Selector */}
            <button
              onClick={handleLocationClick}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                hasLocation
                  ? 'bg-background-secondary hover:bg-border-light text-text-primary'
                  : 'bg-brand-primary/10 hover:bg-brand-primary/20 text-brand-primary'
              }`}
            >
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-medium">
                {getShortLocationName(userLocation)}
              </span>
            </button>

            {/* User Menu or Login Button */}
            {showUserMenu ? (
              <UserMenu user={displayUser} onLogout={logout} />
            ) : (
              <Button
                variant={ButtonVariant.PRIMARY}
                buttonStyle={ButtonStyle.FILLED}
                size="sm"
                onClick={handleLogin}
              >
                Login
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-text-secondary"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-3 border-t border-border-light">
            {/* User Info - Mobile (if authenticated) */}
            {showUserMenu && (
              <div className="px-4 py-3 mb-2 rounded-lg bg-background-secondary">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-primary flex items-center justify-center flex-shrink-0">
                    {displayUser.avatar ? (
                      <img
                        src={displayUser.avatar}
                        alt={displayUser.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-white font-semibold">
                        {displayUser.name
                          .split(' ')
                          .map(part => part[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2)}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Body className="font-semibold text-text-primary truncate">
                      {displayUser.nickname || displayUser.name}
                    </Body>
                    {displayUser.phoneNumber && (
                      <Body className="text-sm text-text-tertiary truncate">
                        {displayUser.phoneNumber}
                      </Body>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Location Selector - Mobile */}
            <button
              onClick={() => {
                handleLocationClick()
                setMobileMenuOpen(false)
              }}
              className={`flex items-center gap-3 w-full px-4 py-2 rounded-lg transition-colors ${
                hasLocation
                  ? 'bg-background-secondary text-text-primary'
                  : 'bg-brand-primary/10 text-brand-primary'
              }`}
            >
              <MapPin className="w-5 h-5" />
              <Body className="font-medium">
                {getShortLocationName(userLocation)}
              </Body>
            </button>

            {/* Account links - Mobile (desktop has UserMenu instead) */}
            {isAuthenticated && (
              <>
                {accountItems.map((item) => {
                  const Icon = item.icon
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        trackNav(item.id, 'hamburger')
                        navigate(item.path)
                        setMobileMenuOpen(false)
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2 text-text-secondary hover:text-brand-primary hover:bg-background-secondary transition-colors rounded-lg"
                    >
                      <Icon className="w-5 h-5" />
                      <Body className="font-medium">{item.label}</Body>
                    </button>
                  )
                })}
              </>
            )}

            {/* Login/Logout Button - Mobile */}
            <div className="px-4 pt-2">
              {isAuthenticated ? (
                <Button
                  variant={ButtonVariant.SECONDARY}
                  buttonStyle={ButtonStyle.CLEAR}
                  onClick={() => {
                    logout()
                    setMobileMenuOpen(false)
                  }}
                  className="w-full"
                >
                  Sair
                </Button>
              ) : (
                <Button
                  variant={ButtonVariant.PRIMARY}
                  buttonStyle={ButtonStyle.FILLED}
                  onClick={() => {
                    handleLogin()
                    setMobileMenuOpen(false)
                  }}
                  className="w-full"
                >
                  Login
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Auth Modal */}
      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={handleAuthSuccess}
      />

      {/* Self-contained location prompt (used when the page doesn't pass its own
          onLocationClick handler) */}
      <MarketplaceLocationPrompt
        open={locationPromptOpen}
        onClose={() => setLocationPromptOpen(false)}
      />
    </header>
  )
}
